import React from 'react';
import get from 'lodash.get';
import storage from 'dibs-browser-storage';
import { FormattedMessage } from 'react-intl';

const LAST_BANNER_MESSAGE_ID = 'lastBannerMessageId';
const MESSAGE_IMPRESSION_CAPS = 'messageImpressionCaps';

const GLOBAL_FALLBACK_TEXT = (
    <FormattedMessage
        id="dbl.Header.MessageBanner.global_fallback_text"
        defaultMessage="1stdibs is the world's leading marketplace for beautiful and rare objects"
    />
);

const shownIds = {};

export const getTrackingObj = ({ messageItem, action }) => {
    return {
        category: 'promo interaction',
        action,
        label: get(messageItem, 'note.content.title'),
    };
};

const messageItemTemplate = ({ desktop = null, mobile = null }) => {
    return {
        note: {
            serviceId: 'template_id_1',
            content: desktop,
            anchors: [
                {
                    serviceId: 'template_id_2',
                    content: mobile,
                },
            ],
        },
    };
};

const globalFallbackMessage = () => {
    const content = {
        title: 'global fallback',
        message: GLOBAL_FALLBACK_TEXT,
    };
    return messageItemTemplate({
        desktop: content,
        mobile: content,
    });
};

export const getNoteByDevice = messageItem => {
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
        return get(messageItem, 'note.anchors[0]');
    }
    return get(messageItem, 'note');
};

const getNoteId = messageItem => get(getNoteByDevice(messageItem), 'serviceId');

const hasAnyText = messageItem => !!get(getNoteByDevice(messageItem), 'content.message');

const messagePreviewMatch = () => window.location.search.match(/cmsMessagePreview=([^&]+)/);

const messageSetsMatch = () => window.location.search.match(/message-sets:([^&]+)/);

export const isPreview = () => !!(messagePreviewMatch() || messageSetsMatch());

const getMessagesFromUrl = () => {
    const match = messagePreviewMatch();
    if (match) {
        return [messageItemTemplate(JSON.parse(decodeURIComponent(match[1])))];
    }
    return null;
};

const getUserTypeAndSetIdFromUrl = () => {
    const setMatch = messageSetsMatch();
    if (setMatch) {
        const splits = setMatch[1].split('=');
        return {
            userType: splits[0],
            setId: splits.length > 1 ? splits[1] : null,
        };
    }
    return null;
};

export const getPreview = () => {
    return {
        messages: getMessagesFromUrl(),
        setParams: getUserTypeAndSetIdFromUrl(),
    };
};

export const addToShownList = messageItem => {
    const noteId = getNoteId(messageItem);
    if (noteId) {
        shownIds[noteId] = true;
    }
};

export const wasShown = messageItem => !!shownIds[getNoteId(messageItem)];

const increaseImpressionCap = noteId => {
    const { localStorage } = storage;
    const msgImpressCaps = localStorage.getItem(MESSAGE_IMPRESSION_CAPS) || {};

    msgImpressCaps[noteId] = msgImpressCaps[noteId] ? msgImpressCaps[noteId] + 1 : 1;

    localStorage.setItem(MESSAGE_IMPRESSION_CAPS, msgImpressCaps);
};

const setNoteIdToStorage = noteId => {
    const { localStorage } = storage;
    localStorage.setItem(LAST_BANNER_MESSAGE_ID, noteId);
};

export const setValuesToStorage = messageItem => {
    const noteId = getNoteId(messageItem);
    if (noteId) {
        increaseImpressionCap(noteId);
        setNoteIdToStorage(noteId);
    }
};

const validImpressionCap = messageItem => {
    const noteId = getNoteId(messageItem);
    const { localStorage } = storage;
    const msgImpressCaps = localStorage.getItem(MESSAGE_IMPRESSION_CAPS);
    if (msgImpressCaps && msgImpressCaps[noteId]) {
        return messageItem.impressionCap > msgImpressCaps[noteId];
    }
    return true;
};

const getNextMessageIndex = (allMessages, validMessages) => {
    const { localStorage } = storage;
    const lastMsgId = localStorage.getItem(LAST_BANNER_MESSAGE_ID);
    if (lastMsgId) {
        const lastMsgIndex = allMessages.findIndex(
            item => getNoteId(item) === lastMsgId.toString()
        );
        for (let i = 0; i < allMessages.length; i++) {
            const nextMsgItem = allMessages[(i + (lastMsgIndex + 1)) % allMessages.length];
            const indexInValidArray = validMessages.findIndex(
                item => getNoteId(item) === getNoteId(nextMsgItem)
            );
            if (indexInValidArray !== -1) {
                return indexInValidArray;
            }
        }
    }
    return 0;
};

const countPrimaryMessages = messageSet => {
    return messageSet ? messageSet[0].items.filter(item => hasAnyText(item)).length : 0;
};

const getValidMessagesArray = (messages, primaryCount) => {
    return messages
        .filter(item => hasAnyText(item) && validImpressionCap(item))
        .slice(0, primaryCount);
};

const getFallbackMessagesArray = messageSet => {
    let fallback = get(messageSet, '[2].items', []).filter(item => hasAnyText(item));
    if (!fallback || !fallback.length) {
        fallback = [globalFallbackMessage()];
    }
    return fallback;
};

const moveItemToFront = (messages, index) => {
    return messages.splice(index).concat(messages);
};

export const getMessagesToShow = messageSet => {
    const primaryCount = countPrimaryMessages(messageSet);
    if (primaryCount) {
        const allMessages = messageSet[0].items.concat(messageSet[1].items);
        const validMessages = getValidMessagesArray(allMessages, primaryCount);
        if (validMessages && validMessages.length) {
            return moveItemToFront(validMessages, getNextMessageIndex(allMessages, validMessages));
        }
    }
    return getFallbackMessagesArray(messageSet);
};
